import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Home as HomeIcon, Building2, Factory, Landmark, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { submitDevis } from "@/lib/api";

const BUILDINGS = [
  { id: "Maison individuelle", icon: HomeIcon },
  { id: "Immeuble collectif", icon: Building2 },
  { id: "Bâtiment tertiaire", icon: Factory },
  { id: "Patrimoine ancien", icon: Landmark },
];

const PRESTATIONS = [
  "Couverture ardoise",
  "Couverture zinc",
  "Étanchéité toiture terrasse / cintrée",
  "Isolation / bardage",
  "Zinguerie / gouttières",
  "Autre (fenêtre de toit, photovoltaïque…)",
];

const RATES = {
  "Couverture ardoise": [90, 160],
  "Couverture zinc": [120, 190],
  "Étanchéité toiture terrasse / cintrée": [60, 110],
  "Isolation / bardage": [80, 140],
  "Zinguerie / gouttières": [50, 90],
};

const TIMELINES = ["Dès que possible", "Sous 1 mois", "1 à 3 mois", "Je planifie"];

const STEP_LABELS = ["Bâtiment", "Prestation", "Surface", "Délais", "Contact"];

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n / 100) * 100);

export default function Devis() {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    building_type: "",
    service_type: "",
    surface: "",
    location: "",
    timeline: "",
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const estimate = useMemo(() => {
    const rate = RATES[form.service_type];
    const surface = parseFloat(form.surface);
    if (!rate || !surface || surface <= 0) return null;
    return { min: fmt(rate[0] * surface), max: fmt(rate[1] * surface) };
  }, [form.service_type, form.surface]);

  const canNext = [
    !!form.building_type,
    !!form.service_type,
    !!form.surface && parseFloat(form.surface) > 0,
    !!form.location && !!form.timeline,
    !!form.name && /.+@.+\..+/.test(form.email) && !!form.phone,
  ][step];

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await submitDevis(form);
      setDone(data);
      toast.success("Demande de devis envoyée", {
        description: "Notre équipe vous rappelle sous 48 h ouvrées.",
      });
    } catch {
      toast.error("Envoi impossible", { description: "Réessayez ou appelez-nous au 02 97 38 05 40." });
    } finally {
      setSending(false);
    }
  };

  const slide = {
    initial: { opacity: 0, x: 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  };

  return (
    <main data-testid="devis-page" className="bg-roch-navy">
      <Seo
        title="Devis en ligne — LE ROCH"
        siteName="LE ROCH"
        description="Devis couvreur à Pontivy : estimez votre projet de couverture ou d'étanchéité en 2 minutes — bâtiment, prestation, surface, délais. Réponse sous 48 h ouvrées."
      />

      <section className="grain relative flex min-h-screen items-center overflow-hidden py-32">
        <div className="pointer-events-none absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-roch-royal/30 blur-[120px]" />
        <div className="pointer-events-none absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-roch-orange/15 blur-[110px]" />

        <div className="relative mx-auto grid w-full max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:gap-20 lg:px-8">
          {/* Left rail */}
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange">Espace devis</p>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
              Votre estimation<br />en <span className="text-roch-orange">2 minutes.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/55">
              Cinq étapes, zéro engagement. Un compagnon LE ROCH étudie votre
              demande et vous rappelle sous 48 h ouvrées avec un chiffrage précis.
            </p>
            <ol className="mt-10 hidden flex-col gap-5 lg:flex">
              {STEP_LABELS.map((label, i) => (
                <li key={label} className="flex items-center gap-4">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full border font-mono text-xs font-semibold transition-colors duration-300 ${
                      i < step
                        ? "border-roch-orange bg-roch-orange text-white"
                        : i === step
                          ? "border-roch-orange text-roch-orange"
                          : "border-white/15 text-white/35"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className={`font-display text-sm font-bold ${i <= step ? "text-white" : "text-white/35"}`}>
                    {label}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Wizard */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-10" data-testid="devis-wizard">
            {done ? (
              <motion.div
                data-testid="devis-success-modal"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-10 text-center"
              >
                <CheckCircle2 className="h-16 w-16 text-roch-orange" />
                <h2 className="mt-6 font-display text-2xl font-extrabold text-white sm:text-3xl">
                  Demande bien reçue, {form.name.split(" ")[0]}.
                </h2>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/55">
                  Référence <span className="font-mono text-roch-orange">{done.id.slice(0, 8).toUpperCase()}</span> —
                  un compagnon vous recontacte sous 48 h ouvrées au {form.phone}.
                </p>
                <div className="mt-8 w-full rounded-2xl border border-white/10 bg-white/5 p-5 text-left">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Récapitulatif</p>
                  <p className="mt-2 text-sm text-white/80">
                    {form.service_type} · {form.building_type} · {form.surface} m² · {form.location}
                  </p>
                </div>
              </motion.div>
            ) : (
              <>
                {/* Progress */}
                <div className="flex gap-2" data-testid="devis-progress">
                  {STEP_LABELS.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-roch-orange" : "bg-white/10"}`}
                    />
                  ))}
                </div>

                <div className="mt-8 min-h-[380px]">
                  <AnimatePresence mode="wait">
                    {step === 0 && (
                      <motion.div key="s0" {...slide}>
                        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Quel type de bâtiment ?</h2>
                        <div className="mt-6 grid grid-cols-2 gap-4" data-testid="devis-step-1-building-type">
                          {BUILDINGS.map((b) => (
                            <button
                              key={b.id}
                              data-testid={`devis-building-${b.id.toLowerCase().replace(/[^a-z]/g, "-")}`}
                              onClick={() => set("building_type", b.id)}
                              className={`flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all duration-300 ${
                                form.building_type === b.id
                                  ? "border-roch-orange bg-roch-orange/15"
                                  : "border-white/10 bg-white/5 hover:border-white/30"
                              }`}
                            >
                              <b.icon className={`h-6 w-6 ${form.building_type === b.id ? "text-roch-orange" : "text-white/50"}`} />
                              <span className="font-display text-sm font-bold text-white">{b.id}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 1 && (
                      <motion.div key="s1" {...slide}>
                        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Quelle prestation ?</h2>
                        <div className="mt-6 grid gap-3" data-testid="devis-step-2-service-select">
                          {PRESTATIONS.map((p) => (
                            <button
                              key={p}
                              data-testid={`devis-service-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
                              onClick={() => set("service_type", p)}
                              className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                                form.service_type === p
                                  ? "border-roch-orange bg-roch-orange/15"
                                  : "border-white/10 bg-white/5 hover:border-white/30"
                              }`}
                            >
                              <span className="font-display text-sm font-bold text-white sm:text-base">{p}</span>
                              <span className={`h-3 w-3 rounded-full ${form.service_type === p ? "bg-roch-orange" : "bg-white/15"}`} />
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div key="s2" {...slide}>
                        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Surface approximative ?</h2>
                        <div className="mt-8">
                          <div className="flex items-baseline gap-3">
                            <input
                              data-testid="devis-step-3-surface-input"
                              type="number"
                              min="1"
                              value={form.surface}
                              onChange={(e) => set("surface", e.target.value)}
                              placeholder="150"
                              className="w-40 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 font-display text-3xl font-extrabold text-white outline-none transition-colors focus:border-roch-orange"
                            />
                            <span className="font-display text-xl font-bold text-white/50">m²</span>
                          </div>
                          <input
                            type="range"
                            min="10"
                            max="2000"
                            step="10"
                            value={form.surface || 0}
                            onChange={(e) => set("surface", e.target.value)}
                            className="mt-6 w-full accent-roch-orange"
                            data-testid="devis-surface-slider"
                          />
                          <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                            <span>10 m²</span>
                            <span>2 000 m²</span>
                          </div>
                        </div>
                        {estimate && (
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-8 rounded-2xl border border-roch-orange/40 bg-roch-orange/10 p-5"
                            data-testid="devis-estimate"
                          >
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-roch-orange">Estimation indicative</p>
                            <p className="mt-2 font-display text-2xl font-extrabold text-white">
                              {estimate.min} € — {estimate.max} €
                            </p>
                            <p className="mt-1 text-xs text-white/45">Fourchette hors aides, affinée après visite technique.</p>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div key="s3" {...slide}>
                        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Où et pour quand ?</h2>
                        <input
                          data-testid="devis-step-4-location-input"
                          type="text"
                          value={form.location}
                          onChange={(e) => set("location", e.target.value)}
                          placeholder="Ville ou code postal — ex. 56300 Pontivy"
                          className="mt-6 w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-roch-orange"
                        />
                        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Délai souhaité</p>
                        <div className="mt-4 flex flex-wrap gap-3">
                          {TIMELINES.map((t) => (
                            <button
                              key={t}
                              data-testid={`devis-timeline-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                              onClick={() => set("timeline", t)}
                              className={`rounded-full px-5 py-2.5 font-display text-sm font-bold transition-all duration-300 ${
                                form.timeline === t
                                  ? "bg-roch-orange text-roch-navy"
                                  : "border border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {step === 4 && (
                      <motion.div key="s4" {...slide}>
                        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Vos coordonnées</h2>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                          <input
                            data-testid="devis-step-5-contact-name-input"
                            type="text"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Nom complet"
                            className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-roch-orange"
                          />
                          <input
                            data-testid="devis-step-5-contact-phone-input"
                            type="tel"
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            placeholder="Téléphone"
                            className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-roch-orange"
                          />
                          <input
                            data-testid="devis-step-5-contact-email-input"
                            type="email"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="Email"
                            className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-roch-orange sm:col-span-2"
                          />
                          <textarea
                            data-testid="devis-message-input"
                            value={form.message}
                            onChange={(e) => set("message", e.target.value)}
                            placeholder="Précisions utiles : hauteur, accès, état actuel… (optionnel)"
                            rows={3}
                            className="resize-none rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-roch-orange sm:col-span-2"
                          />
                        </div>
                        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Récapitulatif</p>
                          <p className="mt-2 text-sm leading-relaxed text-white/75">
                            {form.service_type} · {form.building_type} · {form.surface} m² · {form.location} · {form.timeline}
                            {estimate && <span className="block mt-1 text-roch-orange font-semibold">Estimation : {estimate.min} € — {estimate.max} €</span>}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Nav */}
                <div className="mt-8 flex items-center justify-between">
                  <button
                    data-testid="devis-back-button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 font-display text-sm font-bold text-white/50 transition-colors hover:text-white disabled:opacity-0"
                  >
                    <ArrowLeft className="h-4 w-4" /> Retour
                  </button>
                  {step < 4 ? (
                    <button
                      data-testid="devis-next-button"
                      onClick={() => canNext && setStep((s) => s + 1)}
                      disabled={!canNext}
                      className="group inline-flex items-center gap-2 rounded-full bg-roch-orange px-7 py-3.5 font-display text-sm font-bold text-roch-navy transition-all duration-300 hover:bg-roch-orangeHover disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      Continuer
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      data-testid="devis-form-submit-button"
                      onClick={submit}
                      disabled={!canNext || sending}
                      className="inline-flex items-center gap-2 rounded-full bg-roch-orange px-7 py-3.5 font-display text-sm font-bold text-roch-navy transition-all duration-300 hover:bg-roch-orangeHover disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
